import React, { useEffect, useState, useContext, useMemo } from "react";
import { useEnglishText } from "../../../text/useEnglishText.js";

import DocumentsList from "../../../components/layout/DocumentsList";
import LicenseDetailsModal from "./LicenseDetailsModal";
import { useNavigate } from "react-router-dom";
import {
  getPartitionLicenses,
  getLicenseDetail,
} from "../../../services/licenseService";
import { AuthContext } from "../../../context/AuthContext";
import { Box, Button } from "@mui/material";
import { Button as ListButton } from "../../../components/ui/button";
import { forgotPassword } from "../../../services/authService";
import { useSnackbar } from "../../../components/common/AppSnackbar";
import AdminPageHeader from "../../../components/common/AdminPageHeader";
import EmptyState from "../../../components/common/EmptyState";
import { ShieldCheck } from "lucide-react";

const isEmptyString = (value) =>
  value === null || value === undefined || String(value).trim() === "";
const LicensesPage = () => {
  const { t } = useEnglishText();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState("1");
  const [pageSize] = useState("10");

  const [selectedLicense, setSelectedLicense] = useState(null);
  const [licenseDetail, setLicenseDetail] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  const hasNoActiveLicense = useMemo(() => {
    const setupCompleted = user?.SETUP_COMPLETED;
    const trialEligible = user?.TRIAL_ELIGIBLE;
    const trialStartDate = user?.TRIAL_START_DATE;
    const trialEndDate = user?.TRIAL_END_DATE;
    const url = user?.URL;

    return (
      isEmptyString(setupCompleted) &&
      isEmptyString(trialEligible) &&
      isEmptyString(trialStartDate) &&
      isEmptyString(trialEndDate) &&
      isEmptyString(url)
    );
  }, [user]);
  const loadLicenses = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getPartitionLicenses();
      setRows(Array.isArray(data) ? data : (data?.data ?? []));
    } catch (e) {
      setError(t("licenses.licenses.errors.fetchList"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLicenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleResetPassword = async () => {
    const email =
      user?.USER_EMAIL ||
      user?.user_email ||
      user?.EMAIL ||
      user?.email ||
      licenseDetail?.USER_EMAIL ||
      selectedLicense?.USER_EMAIL ||
      "";

    if (!email) {
      showError(
        t(
          "licenses.passwordReset.emailNotFound",
          "We could not find an email address for your account.",
        ),
      );

      throw new Error("User email was not found.");
    }

    try {
      const response = await forgotPassword({
        email,
      });

      if (String(response?.ERROR_NUMBER) === "0") {
        showSuccess(
          response?.ERROR_MESSAGE ||
            t(
              "licenses.passwordReset.success",
              "A password reset link has been sent to your email address.",
            ),
        );

        return response;
      }

      const message =
        response?.ERROR_MESSAGE ||
        t(
          "licenses.passwordReset.error",
          "Unable to send the password reset link.",
        );

      showError(message);
      throw new Error(message);
    } catch (error) {
      console.error("Password reset error:", error);

      if (!error?.message || error.message === "Failed to fetch") {
        showError(
          t(
            "licenses.passwordReset.error",
            "Unable to send the password reset link.",
          ),
        );
      }

      throw error;
    }
  };

  const handleSelect = async (item) => {
    setSelectedLicense(item);
    setOpenModal(true);
    setDetailLoading(true);

    try {
      const detail = await getLicenseDetail(item.PARTITION_LICENSES_ID);
      setLicenseDetail(detail);
    } catch (e) {
      showError(t("licenses.licenses.errors.fetchDetail"));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLicense(null);
    setLicenseDetail(null);
  };
  if (hasNoActiveLicense) {
    return (
      <Box>
        <AdminPageHeader icon={ShieldCheck} title={t("licenses.licenses.title")} description={t("licenses.licenses.description")} />
        <Box sx={{ maxWidth: 700, mx: "auto" }}>
          <EmptyState
            title={t("licenses.no_active_license_title", "No active license found")}
            description={t("licenses.no_active_license_message", "Submit a license request to continue.")}
            action={<Button variant="outlined" onClick={() => navigate("/dashboard/license-requests")}>{t("licenses.no_active_license_button", "Go to License Requests")}</Button>}
          />
        </Box>
      </Box>
    );
  }
  return (
    <>
      <AdminPageHeader icon={ShieldCheck} title={t("licenses.licenses.title")} description={t("licenses.licenses.description")} />

      <DocumentsList
          data={rows}
          loading={loading}
          error={error}
          emptyTitle={t("licenses.licenses.empty", "No licenses found")}
          onSelect={handleSelect}
          page={parseInt(page)}
          pageSize={parseInt(pageSize)}
          paginateLocally
          onPageChange={(p) => setPage(String(p))}
          actions={<ListButton variant="outline" onClick={() => navigate("/dashboard/license-requests")}>{t("licenses.licenses.actions.buyLicense")}</ListButton>}
      />

      <LicenseDetailsModal
        open={openModal}
        license={licenseDetail}
        loading={detailLoading}
        headerLicense={selectedLicense}
        onClose={handleCloseModal}
        onResetPassword={handleResetPassword}
      />

    </>
  );
};

export default LicensesPage;
